/**
 * Lista de personas (visão default ao entrar na tela · Brief 4 · Tela 2).
 *
 * Colunas com fonte real no serializer: Nome (+ tom derivado de voiceProfile) ·
 * Status · Vertical · Side · Adversarial. As colunas "Modo" e "Conv." do mockup
 * são OMITIDAS — não há campo de modo de operação nem de contagem de conversas
 * no PersonaDto (modo é conceito de Playbook; zero dado falso). O "avatar" do
 * gabarito também é omitido (sem fonte).
 *
 * PR6 fecha a tela: "+ Nova persona" (gate `persona-author`) abre um modal com os
 * campos obrigatórios → cria (`status:"active"`, version 1 · R15) → navega para o
 * editor da nova persona. A ação é defense-in-depth: só renderiza com a capability
 * E o backend a gateia; sem `persona-author` a lista é idêntica à leitura.
 */

import { useState } from 'react';

import { useCapability } from '../../hooks/use-capability';

import { adversarialLabel, sideLabel, verticalLabel, voiceSummary } from './persona-labels';
import { PersonaCreateModal } from './PersonaCreateModal';
import { PersonaStatusPill } from './PersonaStatusPill';

import type { PersonaDto } from './types';

export function PersonaListView({
  personas,
  onOpen,
}: {
  personas: PersonaDto[];
  onOpen: (id: string) => void;
}): JSX.Element {
  const canAuthor = useCapability('persona-author');
  const [showCreate, setShowCreate] = useState(false);
  const isEmpty = personas.length === 0;

  const handleCreated = (created: PersonaDto): void => {
    setShowCreate(false);
    onOpen(created.id);
  };

  return (
    <div className="personas-screen">
      <div className="pe-pagehead">
        <div className="pe-pagehead__heading">
          <div className="pe-pagehead__crumbs">Configuração da operação</div>
          <div className="pe-pagehead__title">
            <span className="pe-pagehead__name">Personas</span>
          </div>
          <div className="pe-pagehead__sub">
            Identidades de voz da IA — tom, vocabulário, lado representado e perfil adversarial
          </div>
        </div>
        {canAuthor && (
          <div className="pe-pagehead__actions">
            <button
              type="button"
              className="pe-btn pe-btn--primary"
              onClick={() => setShowCreate(true)}
            >
              + Nova persona
            </button>
          </div>
        )}
      </div>

      <div className="pe-editor-layout">
        <div className="pe-editor-main">
          {isEmpty ? (
            <div className="pe-panel pe-empty">
              <h2 className="pe-panel__title">Nenhuma persona criada ainda</h2>
              <p className="pe-panel__subtitle">
                {canAuthor
                  ? 'Crie a primeira persona para começar.'
                  : 'Quando personas forem criadas, elas aparecerão aqui.'}
              </p>
              {canAuthor && (
                <button
                  type="button"
                  className="pe-btn pe-btn--primary"
                  onClick={() => setShowCreate(true)}
                >
                  Criar primeira persona
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="pe-list-count">
                {`${personas.length.toString()} ${
                  personas.length === 1 ? 'persona' : 'personas'
                }${canAuthor ? '' : ' · somente leitura'}`}
              </div>
              <table className="pe-listtable">
                <thead className="pe-listtable__head">
                  <tr>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Vertical</th>
                    <th>Lado</th>
                    <th>Adversarial</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {personas.map((persona) => {
                    const tom = voiceSummary(persona.voiceProfile);
                    return (
                      <tr key={persona.id} className="pe-listtable__row">
                        <td>
                          <div className="pe-listtable__name">{persona.name}</div>
                          <div className="pe-listtable__meta">
                            v{persona.version}
                            {tom.length > 0 ? ` · ${tom}` : ''}
                          </div>
                        </td>
                        <td>
                          <PersonaStatusPill status={persona.status} />
                        </td>
                        <td>{verticalLabel(persona.vertical)}</td>
                        <td>
                          <span className="pe-mono">{sideLabel(persona.sideDefault)}</span>
                        </td>
                        <td>
                          <span
                            className={
                              'pe-mono' +
                              (persona.adversarialProfileDefault === 'strict'
                                ? ' pe-mono--danger'
                                : '')
                            }
                          >
                            {adversarialLabel(persona.adversarialProfileDefault)}
                          </span>
                        </td>
                        <td>
                          <div className="pe-listtable__actions">
                            <button
                              type="button"
                              className="pe-btn pe-btn--sm"
                              onClick={() => onOpen(persona.id)}
                            >
                              Abrir
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {canAuthor && (
        <PersonaCreateModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
